/**
 * `POST /v1/ops/cron/:handler/run` — manual control-loop re-run (epic #853,
 * issue #862).
 *
 * Every control-loops handler is a thin proxy over existing idempotent
 * `/internal/*` endpoints, so ops-api can trigger the same work directly
 * through its required platform and gateway HTTP endpoints without touching
 * control-loops-worker.
 *
 * `MANUAL_CRON_HANDLERS` is derived BY HAND from the `HANDLERS` registry in
 * `apps/control-loops-worker/src/schedule.ts` (contracts cannot import app
 * code — packages must not depend on apps). When a handler is added to or
 * removed from that registry, update this list in the same PR. The ops-api
 * route holds the handler → internal-endpoint execution plans and has a
 * completeness test against this list.
 */

interface ManualCronHandlerBase {
  /** Handler name exactly as registered in control-loops `HANDLERS`. */
  readonly name: string;
}

export interface ManualCronRunnableHandler extends ManualCronHandlerBase {
  readonly runnable: true;
  /**
   * Optional operator-facing warning, shown next to the "Run now" control.
   *
   * ── ABSENCE PROMISES NOTHING. READ THIS BEFORE TRUSTING A BLANK CARD. ─────
   * This field is present on the handlers someone has DELIBERATELY JUDGED
   * worth calling out. It is not the output of an audit, and no process keeps
   * it complete: a handler with no warning has simply never been assessed, or
   * was assessed before it grew a side effect.
   *
   * It is therefore NOT safe to read a missing warning as "this handler does
   * nothing outside the system". Several handlers that push to Lark carry no
   * warning today — `serviceability_freshness_check` says "may send one card"
   * in its own catalog entry below, and the reconcile and rating handlers have
   * outward-facing arms of their own. An operator who learns "the page warns
   * me before anything escapes" will press one of those on staging and put a
   * card in that environment's ops channel.
   *
   * The earlier wording here claimed the field was present on every handler
   * whose manual run reaches outside the system in every environment. That
   * turned a curated hint into a guarantee the data does not keep — which is
   * the more dangerous of the two failure modes, because it is the one an
   * operator acts on. Auditing all of the registered handlers for outward
   * effects is tracked separately; until that lands, treat a blank card as
   * "unknown", never as "safe".
   *
   * When present, it states the CONSEQUENCE, never the mechanism: the operator
   * about to press the button needs to know what leaves the building, not which
   * layer holds the environment gate. No counts, no durations — those depend on
   * the data at the moment of the run.
   */
  readonly sideEffectWarning?: string;
}

export interface ManualCronExcludedHandler extends ManualCronHandlerBase {
  readonly runnable: false;
  /** Why manual re-run is not offered. Returned verbatim in the 501 error. */
  readonly reason: string;
}

export type ManualCronHandlerInfo = ManualCronRunnableHandler | ManualCronExcludedHandler;

/**
 * All registered control-loop handlers, in `schedule.ts` registry order.
 * (The count was already two behind before #1918 added the 34th; nothing
 * asserts it, which is why it decayed.)
 *
 * Runnable = the handler's whole effect is one or two calls to idempotent
 * `/internal/*` endpoints that ops-api can reach through authenticated HTTP.
 * Two-phase handlers (gateway read → platform write) are runnable only
 * because the ops-api route orchestrates BOTH steps — never half-runs.
 *
 * Excluded handlers fall into three buckets:
 *   - external side inputs owned by control-loops-worker (fx_rate_refresh,
 *     gateway_public_canary);
 *   - observation-only handlers whose sole output is a worker log line
 *     (supplier_concentration);
 *   - multi-leg cross-region protocols that must not be half-run
 *     (usage_logs_cn_cleanup: Tokyo uplink gate → CN select → Tokyo
 *     confirm-present → CN delete of the confirmed subset).
 */
export const MANUAL_CRON_HANDLERS = [
  { name: 'usage_rollup', runnable: true },
  { name: 'session_cleanup', runnable: true },
  { name: 'public_status_rollup', runnable: true },
  { name: 'billing_anomalies_replay', runnable: true },
  { name: 'gateway_audit_mirror_drain', runnable: true },
  {
    name: 'fx_rate_refresh',
    runnable: false,
    reason:
      'fetches external market-rate sources with parsing logic owned by control-loops-worker; ops-api has no internal endpoint for the fetch phase, so a proxy would half-run the write phase without fresh data',
  },
  {
    name: 'gateway_public_canary',
    runnable: false,
    reason:
      'requires canary credentials and the public gateway base URL, which are configured only in control-loops-worker env',
  },
  {
    name: 'supplier_concentration',
    runnable: false,
    reason:
      'observation-only handler: computes a concentration snapshot into worker logs; there is no state change to re-run',
  },
  { name: 'payment_order_expirer', runnable: true },
  { name: 'cli_probe_scheduler', runnable: true },
  { name: 'target_probe_scheduler', runnable: true },
  { name: 'message_trace_cleanup', runnable: true },
  // #1396 C3: notification-stream retention. Idempotent — a bounded pass
  // deletes only rows quiet past the 90-day window, so a manual re-run
  // finds strictly less to do.
  { name: 'notifications_cleanup', runnable: true },
  // #1396 phase 2 / #1395 B2: budget + balance-runway producers. Idempotent —
  // events dedup on (org, fingerprint, bucket), so a manual re-run only
  // advances last_seen_at.
  { name: 'budget_runway_scan', runnable: true },
  // #2459 A3: promoter rebate FIFO settlement. Idempotent — a settled
  // record is no longer pending, and settle txs unique on
  // `promoter-rebate:<record_id>`, so a manual re-run cannot double-credit.
  { name: 'promoter_settlement_scan', runnable: true },
  // #2460 A4: promoter grant-expiry reminder. Idempotent — stamp
  // `grant_expiry_notified_at` plus fingerprint uniqueness, so a manual
  // re-run cannot re-mail a lot already notified.
  //
  // SIDE EFFECT: sends grant-expiry mail to bound C-end accounts of the
  // environment you trigger it in.
  {
    name: 'promoter_grant_expiry_scan',
    runnable: true,
    sideEffectWarning:
      'Really sends grant-expiry mail to bound C-end accounts of the environment you trigger it in — dev and staging send for real too, not just production. A mail that has gone out cannot be recalled.',
  },
  { name: 'gift_expiry_sweep', runnable: true },
  { name: 'procurement_rise_check', runnable: true },
  { name: 'batch_allocator', runnable: true },
  // Idempotent: a row it settles is no longer unbatched, so a manual
  // re-run finds nothing to move (#1528).
  { name: 'overflow_catchup', runnable: true },
  { name: 'capacity_depletion_sync', runnable: true },
  { name: 'batch_threshold_alert', runnable: true },
  { name: 'vps_metrics_sample', runnable: true },
  // #1967 R7 / #1996. Idempotent: a pass writes one sample row per link and
  // pages Lark only on a status transition. Unarmed environments stay silent.
  { name: 'replication_health_sample', runnable: true },
  // Idempotent by construction: a clean reconcile round writes nothing, so a
  // manual re-run is always safe (storage-autonomy P1, #1408).
  { name: 'pub_config_reconcile', runnable: true },
  { name: 'generate_billing_statements', runnable: true },
  // Idempotent: ending a grant is a status flip, one-shot insert is unique
  // on the source grant, and events unique on (grant, type, audience). A
  // missed tick is extra exposure, so the handler is runnable.
  { name: 'expire_temporary_credit', runnable: true },
  // Idempotent: stage upsert + unique events. A missed tick is extra
  // exposure (overdue customers keep spending), so the handler is
  // runnable — silence here is the worst failure of #2007.
  { name: 'advance_credit_overdue_ladder', runnable: true },
  // Idempotent: a delivery_key row is the stamp, written only after the
  // webhook returns true. A missed tick is a delayed card, so the
  // handler is runnable. SIDE EFFECT: posts into the credit Feishu chat
  // of the environment you trigger it in.
  {
    name: 'deliver_credit_alerts',
    runnable: true,
    sideEffectWarning:
      'Really posts credit-exposure cards into the Lark credit channel of the environment you trigger it in, and stamps those deliveries so the next pass stays quiet. It mails no customer. Unset LARK_CREDIT_WEBHOOK_URL sends nothing and does not fall back to the ops or support chats.',
  },
  { name: 'commission_accrual', runnable: true },
  // Two-phase (gateway read → platform write), runnable only because the
  // ops-api route drives BOTH steps. Idempotent under replay: the fold
  // re-reads a two-hour window and the platform write is monotone, so
  // re-offering a stamp platform already holds updates nothing
  // (storage-autonomy P2, #1409).
  { name: 'api_key_last_used_fold', runnable: true },
  // Idempotent by construction, same as its P1 sibling: a clean round writes
  // nothing. Worth having manually runnable — this handler's silence is
  // TENANT-VISIBLE, because `pub_auth_publish_state.verified_at` is refreshed
  // only here, and once it ages past the watermark bound the gateway answers
  // 503 instead of 401 for unknown key hashes (#1409).
  { name: 'pub_auth_reconcile', runnable: true },
  // Idempotent: an unrated event is re-derivable from the PG anti-join, and
  // every rating write is keyed on request_id (the rated-row PK and
  // balance_transactions.external_request_id), so a manual re-run can
  // double-charge nothing (storage-autonomy P3, #1410).
  { name: 'rating_drain', runnable: true },
  // Idempotent for the same reason, and worth having manually runnable: this
  // handler's silence is REVENUE-VISIBLE — it is the only path that recovers an
  // event whose queue entry was trimmed or whose drain tick was missed.
  { name: 'rating_backstop_sweep', runnable: true },
  // Idempotent, and since #2375 / #2354 a manual run is select-only: the
  // gateway reports the rated-past-retention page it would reap and deletes
  // nothing. The delete itself happens only inside the control-loops handler,
  // CN before Tokyo. Never touches an unrated event (#1410).
  { name: 'rating_events_cleanup', runnable: true },
  // Idempotent: age on the gateway's own `request_lifecycle_events` is the
  // whole predicate, and one call is bounded to a few thousand rows, so a
  // manual run is one extra tick of the same reaper and can never over-delete
  // (#2243). Nothing in the billing chain reads that table.
  { name: 'lifecycle_events_cleanup', runnable: true },
  // Idempotent: age plus a NOT EXISTS guard on the gateway's own
  // `request_lifecycle_metrics` is the whole predicate, and one call is
  // bounded to a few thousand rows, so a manual run is one extra tick of
  // the same reaper and can never over-delete (#2375). Nothing in the
  // billing chain reads that table. Rows that still have event children
  // are skipped, not cascaded.
  { name: 'lifecycle_metrics_cleanup', runnable: true },
  // Three-leg protocol (Tokyo uplink gate → CN select → Tokyo
  // confirm-present → CN delete of the confirmed subset) cannot be
  // driven by a single POST. A half-run that deleted without
  // confirming would be the failure mode this reaper exists to
  // prevent. Operators arm/disarm via USAGE_LOGS_CN_CLEANUP_ARMED.
  {
    name: 'usage_logs_cn_cleanup',
    runnable: false,
    reason:
      'three-leg protocol (Tokyo uplink gate, CN select, Tokyo confirm-present, CN delete) cannot be driven by a single POST',
  },
  // Copy-only Tokyo archiver: one POST to the gateway copy route, then
  // optionally one equivalence POST. Driven by control-loops so a
  // half-run from ops-api cannot skip the equivalence check. Operators
  // arm/disarm via USAGE_LOGS_JP_ARCHIVE_COPY_ARMED.
  {
    name: 'usage_logs_jp_archive_copy',
    runnable: false,
    reason:
      'copy-only archiver is driven by control-loops; arm via USAGE_LOGS_JP_ARCHIVE_COPY_ARMED',
  },
  // Tokyo archive delete: one POST to the gateway delete route with the
  // soak from wrangler vars. Driven by control-loops so a half-run from
  // ops-api cannot skip the soak / archive-membership re-check. Operators
  // arm/disarm via USAGE_LOGS_JP_ARCHIVE_DELETE_ARMED.
  {
    name: 'usage_logs_jp_archive_delete',
    runnable: false,
    reason:
      'archive delete is driven by control-loops; arm via USAGE_LOGS_JP_ARCHIVE_DELETE_ARMED',
  },
  // Daily usage_logs partition maintenance (#2559): two metadata-only
  // POSTs (JP gateway, then CN gateway) that create the next 14 days of
  // partitions. Idempotent, but driven by control-loops on its 6-hour
  // cadence; an operator who needs a partition now calls the gateway
  // ensure route directly rather than replaying the fan-out from ops-api.
  {
    name: 'usage_logs_partitions',
    runnable: false,
    reason:
      'partition maintenance is driven by control-loops every 6 hours; call POST /internal/usage-logs/partitions/ensure on the gateway for an immediate run',
  },
  // Idempotent by construction: every pass is a full REPEATABLE READ
  // recompute + diff against the published rows, so a manual run converges to
  // the same projection the next scheduled pass would. Worth having manually
  // runnable — the projection refreshes on a 10-minute cadence, and after an
  // ops routing change this is the lever that collapses the catalog lag to
  // now instead of the next tick (#1405).
  { name: 'pub_serviceability_recompute', runnable: true },
  // #1405 PR-3. Runnable, and worth being runnable: the projection it watches
  // is what `/v1/catalog/me` and the five key-write gates read, so an operator
  // who has just fixed a stalled writer wants to confirm the freshness verdict
  // without waiting out a 10-minute cadence. Its whole effect is one POST to an
  // idempotent platform endpoint that reads one row and may send one card.
  { name: 'serviceability_freshness_check', runnable: true },
  // #1867 P1. Runnable, and the manual lever matters more here than for most:
  // this handler is gated to production INSIDE control-loops, so a scheduled
  // run never happens anywhere else, and without a manual trigger there is no
  // way to exercise the money alert at all before an incident needs it.
  //
  // SIDE EFFECT AN OPERATOR MUST KNOW: ops-api calls the platform route
  // directly, which is NOT environment-gated (the gate is the control-loops
  // handler's). Triggering this in dev or staging therefore really does post to
  // the Lark ops webhook configured for that environment, using that
  // environment's toy funding batches. Every card names its environment on the
  // first line, so the noise is identifiable rather than misleading — but it is
  // a real send, not a dry run.
  //
  // Idempotent: the durable `upstream_runway_alerts` rows dedup per crossing,
  // so a manual re-run during an active alert refreshes observations and sends
  // nothing.
  {
    name: 'upstream_runway_alert',
    runnable: true,
    sideEffectWarning:
      "Really posts a runway-alert card into the Lark ops channel of the environment you trigger it in — dev and staging push for real too, not just production.",
  },
  // #1876. Runnable for the same reason as its sibling above: the handler is
  // gated to production INSIDE control-loops, so a scheduled run never happens
  // anywhere else and there is otherwise no way to exercise the customer alert
  // before it matters.
  //
  // SIDE EFFECT AN OPERATOR MUST KNOW, AND IT IS SHARPER HERE: ops-api calls
  // the platform route directly, and that route is NOT environment-gated (the
  // gate is the control-loops handler's). Triggering this in dev or staging
  // therefore really does SEND EMAIL to whatever addresses that environment's
  // accounts carry — real inboxes for anyone who signed up there. Unlike the
  // Lark cards, a mail names no environment the recipient would recognise and
  // cannot be recalled.
  //
  // Idempotent: the durable `account_balance_alerts` rows dedup per crossing,
  // so a manual re-run during an active alert refreshes observations and sends
  // nothing.
  {
    name: 'account_balance_alert',
    runnable: true,
    sideEffectWarning:
      "Really sends balance-alert mail to the live addresses on the accounts of the environment you trigger it in — dev and staging send for real too, not just production. A mail that has gone out cannot be recalled.",
  },
  // #1877. Runnable, and unlike its two neighbours it is not production-gated
  // anywhere: the handler runs in every environment, so a manual trigger is a
  // re-run of something that already happens rather than a first exercise.
  //
  // SIDE EFFECT AN OPERATOR MUST KNOW: it posts one Lark card per due notice
  // into the environment's ops channel. What it does NOT do is change any
  // model's shelf status — the flip stays manual, which is the whole point of
  // the notice lifecycle, and the warning says so because "due-date sweep" is
  // exactly the name an operator would expect to mean the opposite.
  //
  // Idempotent: a notice already moved to `pending_execution` is no longer
  // `announced`, so it is outside the sweep's selection and a re-run neither
  // re-cards it nor re-marks it.
  {
    name: 'email_notice_reminder',
    runnable: true,
    sideEffectWarning:
      "Really posts a card per due notice into the Lark ops channel of the environment you trigger it in, and stamps those notices as reminded so the next pass stays quiet. It mails no customer and changes no model — the reminder date drives nothing but this card.",
  },
  // #1918. Compares the global default config's SKU partition against the
  // platform default tables. Read-only at the database — four SELECTs and no
  // write of any kind — so a manual re-run is as safe as a page refresh; the
  // only observable side effect is the card, and only while they disagree.
  {
    name: 'routing_default_parity_check',
    runnable: true,
    sideEffectWarning:
      'Reads only. Posts one card into the Lark ops channel of the environment you trigger it in, and only when the two default surfaces actually disagree — an in-sync comparison is completely silent. Triggering it repeatedly during a standing drift will post a card each time.',
  },
  // Idempotent, and the reason is worth stating because this handler can move
  // money: a task that has already settled is no longer in a live state, so a
  // manual re-run does not see it, and even if it did, `usage_events.request_id`
  // is UNIQUE and the settlement transaction rolls back whole on conflict. The
  // reclaim path writes no ledger row at all. Worth having manually runnable —
  // its silence is TENANT-VISIBLE, because an unreleased in-flight slot is a
  // 429 on every subsequent submission for that account (#1900, ADR-013 §4).
  { name: 'generation_task_reconciler', runnable: true },
  // #2375 / #2354 lane B10. Cross-region fan-out (jp gateway, cn gateway,
  // Tokyo platform) is owned by control-loops; ops-api has no CN transport
  // and a half-run would write a one-sided day. Read rds_storage_snapshots.
  {
    name: 'storage_snapshot',
    runnable: false,
    reason:
      'cross-region fan-out (jp gateway, cn gateway, platform) is driven by control-loops only; read rds_storage_snapshots instead',
  },
  // #2410. Idempotent: a sealed day with a `_days` row is skipped, and a
  // stored day whose live totals differ is reported rather than overwritten.
  // Writes only `finance_pnl_usage_daily_rollup` / `_days` (derived).
  {
    name: 'finance_pnl_materialize',
    runnable: true,
    sideEffectWarning:
      'Writes the finance PnL usage rollup for sealed Asia/Shanghai days. Idempotent: already-materialized days are skipped, and a stored day whose recomputed totals differ is reported as drift and left untouched.',
  },
  // #2472. Idempotent: the attribution key is
  // `credit_draw:<account>:<grant|nogrant>:<businessDate>`; lot totals are
  // re-derived each pass and the uncovered gap is recomputed under FOR UPDATE,
  // excluding lots already allocated to the charge, so a manual re-run cannot
  // double-attribute a cent. A manual run is one page (the shared wire cap);
  // the scheduled handler's own paging loop drains the rest next tick, same
  // shape as `rating_drain` / `generate_billing_statements`. Worth having
  // manually runnable: its silence is REVENUE-VISIBLE
  // (`unallocated_usd_micros` residual returns to 18.2%), not a tenant-facing
  // fault, so an operator who has just deployed wants to confirm it ran
  // rather than wait out a 10-minute tick.
  {
    name: 'credit_draw_sweep',
    runnable: true,
    sideEffectWarning:
      "Writes `balance_credit_lots` (`source_type = 'credit_draw'`) and `balance_charge_allocations`. Idempotent: lots already allocated to a charge are excluded. One page per manual run; the scheduled handler drains the remainder next tick.",
  },
  // #2575. Idempotent: each pass overwrites archive buckets for the
  // lookback window. Writes only `codex_capacity_buckets` (and prunes
  // past retention). Does not evaluate the alert and does not send
  // any notification.
  {
    name: 'codex_capacity_rollup',
    runnable: true,
    sideEffectWarning:
      'Recomputes Codex-capacity archive buckets for the lookback window and prunes rows past retention. Idempotent overwrite. Does not send any notification.',
  },
  // #2575. Runnable for the same reason as `account_balance_alert`: the
  // handler is gated to production INSIDE control-loops, so a scheduled
  // run never happens anywhere else and there is otherwise no way to
  // exercise the customer alert before it matters.
  //
  // SIDE EFFECT AN OPERATOR MUST KNOW: ops-api calls the platform route
  // directly, and that route is NOT environment-gated (the gate is the
  // control-loops handler's). Triggering this in the environment you
  // are in therefore really does SEND EMAIL to the live addresses on
  // that environment's accounts — dev and staging send for real too,
  // not just production. A mail that has gone out cannot be recalled.
  {
    name: 'codex_capacity_alert',
    runnable: true,
    sideEffectWarning:
      "Really sends Codex-capacity-alert mail to the live addresses on the accounts of the environment you trigger it in — dev and staging send for real too, not just production. A mail that has gone out cannot be recalled.",
  },
] as const satisfies readonly ManualCronHandlerInfo[];

/** Union of handler names that ops-api offers manual re-run for. */
export type ManualCronRunnableHandlerName = Extract<
  (typeof MANUAL_CRON_HANDLERS)[number],
  { runnable: true }
>['name'];

export function findManualCronHandler(
  name: string,
): (typeof MANUAL_CRON_HANDLERS)[number] | undefined {
  return MANUAL_CRON_HANDLERS.find((h) => h.name === name);
}

/**
 * One downstream `/internal/*` call made while executing a manual run.
 * `status === null` means the fetch itself threw (network / binding error).
 */
export interface TriggerCronHandlerStep {
  readonly target: 'gateway' | 'platform';
  readonly endpoint: string;
  readonly status: number | null;
  readonly ok: boolean;
}

/**
 * `POST /v1/ops/cron/:handler/run` 200 body. The HTTP status reflects
 * whether the TRIGGER was accepted and executed — downstream failure is
 * reported through `ok: false` + `error` so the operator still sees which
 * step failed. Cron-run history recording is best-effort; this response is
 * the source of truth for a manual run's outcome.
 */
export interface TriggerCronHandlerResponse {
  readonly handler: ManualCronRunnableHandlerName;
  /** ISO-8601 instant the manual run started. */
  readonly triggered_at: string;
  readonly duration_ms: number;
  readonly ok: boolean;
  readonly steps: readonly TriggerCronHandlerStep[];
  /** Bounded, handler-shaped result digest (mirrors the cron handler's result). */
  readonly summary: unknown;
  readonly error?: string;
}

/**
 * Optional body for `POST /v1/ops/cron/:handler/run` (router#2032).
 *
 * `now_ms` is a test-clock for time-constructed scenarios (temporary
 * credit expiry, overdue ladder). It is environment-gated, not
 * role-gated: production refuses it even for super-admin. Only the
 * handlers in {@link MANUAL_CRON_NOW_MS_HANDLERS} forward it.
 */
export interface TriggerCronHandlerRequest {
  readonly now_ms?: number;
}

/**
 * Manual cron handlers whose downstream `/internal/*` body already
 * accepts `now_ms`. A clock on any other handler is refused rather
 * than silently dropped.
 */
export const MANUAL_CRON_NOW_MS_HANDLERS = [
  'generate_billing_statements',
  'expire_temporary_credit',
  'advance_credit_overdue_ladder',
  'deliver_credit_alerts',
  'commission_accrual',
  'generation_task_reconciler',
] as const satisfies readonly ManualCronRunnableHandlerName[];

export type ManualCronNowMsHandlerName = (typeof MANUAL_CRON_NOW_MS_HANDLERS)[number];

/**
 * Non-production environments that may accept operator test-only writes
 * (manual cron clock, direct ops-user password set). Missing, empty,
 * unknown, and `production` are refused. Copied by the password-set
 * gate (router#2032 round 2) so both writes fail closed the same way.
 */
export const NON_PRODUCTION_OPS_ENVIRONMENTS = ['dev', 'staging', 'test'] as const;

export const CRON_NOW_MS_ENVIRONMENTS = NON_PRODUCTION_OPS_ENVIRONMENTS;

export function nonProductionOpsEnvironmentAllowed(
  environment: string | undefined,
): boolean {
  if (environment === undefined) {
    return false;
  }
  for (const allowed of NON_PRODUCTION_OPS_ENVIRONMENTS) {
    if (allowed === environment) {
      return true;
    }
  }
  return false;
}

export function cronNowMsEnvironmentAllowed(environment: string | undefined): boolean {
  return nonProductionOpsEnvironmentAllowed(environment);
}

/** Direct admin-set password is non-production only (router#2032). */
export function directOpsPasswordSetEnvironmentAllowed(
  environment: string | undefined,
): boolean {
  return nonProductionOpsEnvironmentAllowed(environment);
}

export function manualCronHandlerAcceptsNowMs(
  name: string,
): name is ManualCronNowMsHandlerName {
  for (const handler of MANUAL_CRON_NOW_MS_HANDLERS) {
    if (handler === name) {
      return true;
    }
  }
  return false;
}
